import { SSEEventEmitter } from '../../../shared/sse/SSEEventEmitter';
import { SSEEventPayloadType } from '../types/sse.types';
export class SSEService {
    constructor(options) {
        this.eventEmitter = new SSEEventEmitter();
        this.activeStreams = new Map();
        this.options = options;
    }
    async sendMessage(conversationId, content, model, is_onboarding) {
        const prefix = content == null ? 'cont' : 'tmp';
        const messageId = `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const sseUrl = `${this.options.baseApiUrl}/assistant/conversations/${conversationId}/converse`;
        await this.createMessageStream(conversationId, messageId, sseUrl, content, model, is_onboarding);
        return messageId;
    }
    async createMessageStream(conversationId, messageId, sseUrl, content, model, is_onboarding) {
        var _a, _b;
        this.closeMessageStream(messageId);
        try {
            const response = await fetch(sseUrl, {
                method: 'POST',
                headers: Object.assign({ 'Content-Type': 'application/json', Accept: 'text/event-stream', 'Cache-Control': 'no-cache' }, (this.options.authToken && {
                    Authorization: `Bearer ${this.options.authToken}`,
                })),
                body: JSON.stringify(Object.assign(Object.assign({ message: content !== null && content !== void 0 ? content : null }, (model && { model })), (is_onboarding && { is_onboarding }))),
            });
            if (!response.ok) {
                const errorEvent = {
                    type: SSEEventPayloadType.ASSISTANT_ERROR,
                    conversation_id: conversationId,
                    message_uuid: messageId,
                    payload: {
                        message_uuid: messageId,
                        status_key: 'assistant.connection_error',
                    },
                };
                this.handleStreamEvent(errorEvent);
                const errorMessage = `SSE request failed: ${response.status} ${response.statusText}`;
                this.handleConnectionError(messageId, errorMessage);
                return;
            }
            if (!response.body) {
                const errorMessage = 'Response body is null';
                this.handleConnectionError(messageId, errorMessage);
                throw new Error(errorMessage);
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            this.activeStreams.set(messageId, {
                close: () => {
                    try {
                        reader.cancel().catch(() => {
                        });
                    }
                    catch (_) {
                    }
                },
            });
            await this.processSSEStream(conversationId, messageId, reader, decoder);
        }
        catch (error) {
            console.error('Failed to create SSE stream:', error);
            const errorEvent = {
                type: SSEEventPayloadType.ASSISTANT_ERROR,
                conversation_id: conversationId,
                message_uuid: messageId,
                payload: {
                    message_uuid: messageId,
                    status_key: 'assistant.connection_error',
                },
            };
            this.handleStreamEvent(errorEvent);
            (_b = (_a = this.options).onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
            this.closeMessageStream(messageId);
        }
    }
    async processSSEStream(conversationId, messageId, reader, decoder) {
        var _a, _b;
        let buffer = '';
        let sawAssistantComplete = false;
        let sawAssistantError = false;
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    buffer += decoder.decode();
                    if (buffer.length > 0) {
                        const trailing = buffer;
                        buffer = '';
                        for (const line of trailing.split('\n')) {
                            const eventType = this.processSSELine(conversationId, messageId, line);
                            if (eventType === SSEEventPayloadType.ASSISTANT_COMPLETE) {
                                sawAssistantComplete = true;
                            }
                            else if (eventType === SSEEventPayloadType.ASSISTANT_ERROR) {
                                sawAssistantError = true;
                            }
                        }
                    }
                    if (!sawAssistantComplete && !sawAssistantError) {
                        console.warn('🔥 [SSEService] Stream ended without assistant_complete, synthesizing one');
                        this.handleStreamEvent({
                            type: SSEEventPayloadType.ASSISTANT_COMPLETE,
                            conversation_id: conversationId,
                            message_uuid: messageId,
                            payload: { message_uuid: messageId },
                        });
                    }
                    break;
                }
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                for (const line of lines) {
                    const eventType = this.processSSELine(conversationId, messageId, line);
                    if (eventType === SSEEventPayloadType.ASSISTANT_COMPLETE) {
                        sawAssistantComplete = true;
                    }
                    else if (eventType === SSEEventPayloadType.ASSISTANT_ERROR) {
                        sawAssistantError = true;
                    }
                }
            }
        }
        catch (error) {
            console.error('Error processing SSE stream:', error);
            this.handleStreamEvent({
                type: SSEEventPayloadType.ASSISTANT_ERROR,
                conversation_id: conversationId,
                message_uuid: messageId,
                payload: {
                    message_uuid: messageId,
                    status_key: 'assistant.connection_error',
                },
            });
            (_b = (_a = this.options).onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
        }
        finally {
            this.closeMessageStream(messageId);
        }
    }
    processSSELine(conversationId, messageId, line) {
        if (!line.trim())
            return null;
        if (line.startsWith('data: ')) {
            const data = line.substring(6);
            try {
                const sseEvent = JSON.parse(data);
                const eventType = sseEvent.type;
                const payload = sseEvent.payload || {};
                const mappedEventType = this.mapBackendEventType(eventType);
                const globalEventTypes = new Set([
                    SSEEventPayloadType.CONVERSATION_TITLE_UPDATED,
                    SSEEventPayloadType.ASSISTANT_WARNING,
                    SSEEventPayloadType.ASSISTANT_STATUS,
                    SSEEventPayloadType.ASSISTANT_ERROR,
                    SSEEventPayloadType.ASSISTANT_COMPLETE,
                ]);
                const isGlobalEvent = globalEventTypes.has(mappedEventType);
                const finalMessageUuid = isGlobalEvent
                    ? sseEvent.message_uuid
                    : sseEvent.message_uuid || messageId;
                const processedEvent = {
                    type: mappedEventType,
                    timestamp: sseEvent.timestamp,
                    conversation_id: conversationId,
                    message_uuid: finalMessageUuid,
                    payload: Object.assign({ message_uuid: finalMessageUuid }, payload),
                };
                this.handleStreamEvent(processedEvent);
                return mappedEventType;
            }
            catch (error) {
                console.error('Failed to parse SSE event:', error);
                this.eventEmitter.emit('error', {
                    type: SSEEventPayloadType.ASSISTANT_ERROR,
                    message_uuid: messageId,
                    payload: {
                        message_uuid: messageId,
                        error: 'Failed to parse server response',
                    },
                });
                return null;
            }
        }
        return null;
    }
    handleConnectionError(messageId, error) {
        this.eventEmitter.emit('error', {
            type: SSEEventPayloadType.ASSISTANT_ERROR,
            message_uuid: messageId,
            payload: {
                message_uuid: messageId,
                error,
            },
        });
        this.closeMessageStream(messageId);
    }
    mapBackendEventType(backendType) {
        const enumValues = Object.values(SSEEventPayloadType);
        if (enumValues.includes(backendType)) {
            return backendType;
        }
        console.warn(`[SSEService] Unknown backend event type: ${backendType}, falling back to assistant_message_chunk`);
        return SSEEventPayloadType.ASSISTANT_MESSAGE_CHUNK;
    }
    handleStreamEvent(event) {
        try {
            this.eventEmitter.emit('stream', event);
        }
        catch (error) {
            console.error('Error in handleStreamEvent:', error);
        }
    }
    closeMessageStream(messageId) {
        const stream = this.activeStreams.get(messageId);
        if (stream) {
            stream.close();
            this.activeStreams.delete(messageId);
        }
    }
    on(event, listener) {
        return this.eventEmitter.on(event, listener);
    }
    disconnect() {
        for (const [, stream] of this.activeStreams) {
            stream.close();
        }
        this.activeStreams.clear();
        this.eventEmitter.removeAllListeners();
    }
}
